import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { History, MessageSquare, Trash2, Search, Sparkles, Code, GraduationCap, Languages } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TOOL_ICON = {
  chat: MessageSquare, writing: Sparkles, coding: Code,
  homework: GraduationCap, research: Search, translator: Languages,
};
const TOOL_ROUTE = {
  chat: '/ai/chat', writing: '/ai/writing', coding: '/ai/code',
  homework: '/ai/homework', research: '/ai/research', translator: '/ai/translate',
};

function groupByDate(convs) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
  const groups = { Today: [], Yesterday: [], 'Previous 7 days': [], Older: [] };
  convs.forEach(c => {
    const d = new Date(c.updated_at || c.created_at);
    if (d >= today) groups.Today.push(c);
    else if (d >= yesterday) groups.Yesterday.push(c);
    else if (d >= weekAgo) groups['Previous 7 days'].push(c);
    else groups.Older.push(c);
  });
  return groups;
}

export default function ChatHistory() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/chat/conversations'); setConvs(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user]);

  const filtered = useMemo(
    () => q ? convs.filter(c => (c.title || '').toLowerCase().includes(q.toLowerCase())) : convs,
    [convs, q]
  );
  const groups = useMemo(() => groupByDate(filtered), [filtered]);

  const open = (c) => {
    const base = TOOL_ROUTE[c.tool || 'chat'] || '/ai/chat';
    nav(`${base}?cid=${c.id}`);
  };
  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    await api.delete(`/chat/conversations/${id}`);
    setConvs(prev => prev.filter(c => c.id !== id));
    toast.success('Deleted');
  };
  const rename = async (id, currentTitle, e) => {
    e.stopPropagation();
    const t = window.prompt('New title', currentTitle);
    if (!t) return;
    await api.post(`/chat/conversations/${id}/rename`, { title: t });
    setConvs(prev => prev.map(c => c.id === id ? { ...c, title: t } : c));
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <History className="w-12 h-12 mx-auto text-slate-500 mb-3" />
        <div className="font-display text-xl font-bold">Sign in to see your chats</div>
        <Link to="/login"><Button className="mt-4 gradient-brand text-white">Sign in</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2"><History className="w-6 h-6 text-violet-400" /> Chat History</h1>
          <p className="text-sm text-slate-400">All your saved Jixxeo AI conversations</p>
        </div>
        <Link to="/ai/chat"><Button data-testid="history-new-chat" className="gradient-brand text-white">+ New chat</Button></Link>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="history-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search your chats" className="pl-9" />
      </div>

      {loading && <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <Card className="glass p-10 text-center">
          <MessageSquare className="w-10 h-10 mx-auto text-slate-500 mb-2" />
          <div className="font-display text-lg font-bold">{q ? 'No matches' : 'No chats yet'}</div>
          <div className="text-sm text-slate-400 mt-1">{q ? 'Try another search' : 'Start a conversation to see it here'}</div>
        </Card>
      )}

      {!loading && Object.entries(groups).map(([label, list]) => list.length > 0 && (
        <section key={label}>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</div>
          <Card className="glass overflow-hidden">
            {list.map(c => {
              const Icon = TOOL_ICON[c.tool || 'chat'] || MessageSquare;
              return (
                <div key={c.id} data-testid={`history-conv-${c.id}`} onClick={() => open(c)}
                  className="group flex items-center gap-3 p-3 sm:p-4 border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/5 no-tap-highlight">
                  <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-violet-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.title || 'Untitled'}</div>
                    <div className="text-xs text-slate-400 capitalize">{c.tool || 'chat'} • {new Date(c.updated_at || c.created_at).toLocaleString()}</div>
                  </div>
                  <button data-testid={`history-rename-${c.id}`} onClick={(e) => rename(c.id, c.title, e)} className="text-slate-400 hover:text-white opacity-60 hover:opacity-100 p-1 text-sm">✎</button>
                  <button data-testid={`history-delete-${c.id}`} onClick={(e) => del(c.id, e)} className="text-red-400 hover:text-red-300 p-1 opacity-60 hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </Card>
        </section>
      ))}
    </div>
  );
}
